<i18n>
{
  "cl": {
    "error-getProspectList": "Ocurrió un error al tratar de obtener los prospectos.",
    "back": "Volver",
    "brokerDetailsPageTitle": "Datos del Broker",
    "errorFetchingData": "Ocurrió un error al tratar de obtener los datos",
    "errorBrokerNotFound": "El Broker solicitado no fue encontrado",
    "errorBrokerLeadsNotFound": "Este broker no tiene leads asociados",
    "errorUpdatingStatus": "Ocurrió un error al tratar de actualizar el estado",
    "succesUpdatedStatus": "Estado actualizado con éxito"
  },
  "pe": {
    "error-getProspectList": "Ocurrió un error al tratar de obtener los prospectos.",
    "back": "Volver",
    "brokerDetailsPageTitle": "Datos del Broker",
    "errorFetchingData": "Ocurrió un error al tratar de obtener los datos",
    "errorBrokerNotFound": "El Broker solicitado no fue encontrado",
    "errorBrokerLeadsNotFound": "Este broker no tiene leads asociados",
    "errorUpdatingStatus": "Ocurrió un error al tratar de actualizar el estado",
    "succesUpdatedStatus": "Estado actualizado con éxito"
  },
  "br": {
    "error-getProspectList": "Ocorreu um erro ao tentar obter os leads.",
    "back": "Retorna",
    "brokerDetailsPageTitle": "Dados do Broker",
    "errorFetchingData": "Ocorreu um erro ao tentar obter os dados",
    "errorBrokerNotFound": "O Broker solicitado não foi encontrado",
    "errorBrokerLeadsNotFound": "Este broker não tem leads associados",
    "errorUpdatingStatus": "Ocorreu um erro ao tentar atualizar o status",
    "succesUpdatedStatus": "Status atualizado com sucesso",
    "simulator": "Novo leads"
  }
}
</i18n>

<template>
  <section class="container">
    <header class="broker-details-header">
      <h2 class="broker-details-header-title">
        {{ $t(`brokerDetailsPageTitle`) }}
      </h2>
      <b-button
        v-if="backButton"
        id="return-btn"
        class="is-button-secondary is-small is-rounded is-pulled-right ml-2"
        @click="$router.go(-1)">
        {{ $t("back") }}
      </b-button>
      <b-button
        v-if="showSimulationButton"
        id="simulation-button"
        tag="a"
        class="is-button-primary is-small is-rounded is-pulled-right mb-2"
        icon-left="fa-solid fa-user-plus"
        :href="buildSimulatorFrontUrl()"
        data-cy="simulation-button"
        target="_blank">
        {{ $t("simulator") }}
      </b-button>
    </header>

    <div class="card-item prospect-info mb-3">
      <Spinner v-if="isLoadingBrokerData || isLoadingBrokerProspects" />
      <template v-else>
        <BrokerData
          v-if="broker.name"
          :broker="broker"
          :user-is-broker="userIsBroker"
          :is-updating-status="isUpdatingStatus"
          @updateBrokerStatus="updateBrokerStatus" />
        <template v-if="brokerProspects.length > 0">
          <BrokerLeads :prospects-from-brokers="brokerProspects" />
          <CredituPagination
            v-if="brokerProspects.length > 0"
            :documents-per-page.sync="pagination.documentsPerPage"
            :total="pagination.numberOfDocuments"
            :page-number.sync="pagination.pageNumber"
            size="is-small"
            items-name="Leads"
            :range-before="1"
            :range-after="1"
            :documents-per-page-options="[5, 10, 20, 50]"
            @update:documentsPerPage="catchUpdatePagination('setPaginationDocumentsPerPage', $event)"
            @update:pageNumber="catchUpdatePagination('setPaginationPageNumber', $event)" />
        </template>

        <Message
          v-else
          :message="message.message"
          :icon="message.icon"
          :as-card="false" />
      </template>
    </div>
  </section>
</template>

<script>
import Spinner from '@/ui/components/Spinner.vue';
import CredituPagination from '@/ui/components/CredituPagination.vue';
import { getProspectList } from '@/application/services/api-clients/prospects-leads';
import { convertObjectToBase64 } from '@/domain/helpers';
import { getBrokerById, updateBrokerStatus } from '@/application/services/api-clients/brokers';
import { getFeatureToggleByName } from '@/application/services/api-clients/features-toggles';
import Message from '../../../components/Message.vue';
import BrokerLeads from '../../../components/internal/brokers/BrokerLeads.vue';
import BrokerData from '../../../components/internal/brokers/BrokerData.vue';

export default {
  name: 'BrokerDetails',
  components: { Spinner, CredituPagination, Message, BrokerData, BrokerLeads },
  data() {
    const { brokerId } = this.$route.params;
    const { userIsBroker, brokerIsMultiple } = this.$store.state.brokersModule;

    return {
      showSimulationButton: null,
      brokerId,
      userIsBroker,
      backButton: !userIsBroker || (userIsBroker && brokerIsMultiple),
      broker: {},
      brokerProspects: [],
      isLoadingBrokerData: false,
      isLoadingBrokerProspects: false,
      isUpdatingStatus: false,
      message: {},
      urlSimulatorFrontWithData: ''
    };
  },
  computed: {
    pagination() {
      return this.$store.state.leadsModule.pagination;
    }
  },
  async mounted() {
    this.showSimulationButton = (await getFeatureToggleByName('internal-frontend-comercial-manager-show-simulation-button')).isActive;
    await this.loadBrokerData();
    await this.loadBrokerProspects();
  },
  methods: {
    buildSimulatorFrontUrl() {
      const payload = { brokerId: this.broker._id };
      const base64Payload = convertObjectToBase64(payload);
      return `${process.env.VUE_APP_SIMULATOR_FRONTEND_BR}/br/simulation?payload=${base64Payload}`;
    },
    async updateBrokerStatus(newStatus) {
      const body = {
        status: newStatus,
        brokerId: this.brokerId
      };
      this.isUpdatingStatus = true;
      try {
        await updateBrokerStatus(body);
        this.$buefy.toast.open({
          message: this.$t('succesUpdatedStatus'),
          position: 'is-top-right',
          type: 'is-primary'
        });
      } catch (_error) {
        this.$buefy.toast.open({
          message: this.$t('errorUpdatingStatus'),
          position: 'is-top-right',
          type: 'is-danger'
        });
      }
      this.isUpdatingStatus = false;
    },
    async loadBrokerData() {
      this.isLoadingBrokerData = true;
      try {
        const response = await getBrokerById(this.brokerId);
        const [brokerDetails] = response.brokerDetails;
        if (typeof brokerDetails !== 'undefined') {
          this.broker = brokerDetails;
        } else {
          this.message = {
            message: this.$t('errorBrokerNotFound'),
            icon: 'document-not-found.svg'
          };
        }
      } catch (_error) {
        this.message = {
          message: this.$t('errorFetchingData'),
          icon: 'data_maintenance.svg'
        };
      }
      this.isLoadingBrokerData = false;
    },
    async loadBrokerProspects() {
      this.isLoadingBrokerProspects = true;
      const paramsGetProspectList = {
        ...this.pagination,
        filters: [{ field: 'brokerId', operator: '$eq', values: this.brokerId }]
      };
      try {
        const { prospects, pagination } = await getProspectList(paramsGetProspectList);
        if (prospects && prospects.length > 0) {
          this.brokerProspects = prospects;
          this.$store.dispatch('setCurrentPagination', pagination || {});
        } else if (!prospects && !this.message.message) {
          this.message = {
            message: this.$t('errorBrokerLeadsNotFound'),
            icon: 'document-not-found.svg'
          };
        }
      } catch (_error) {
        this.message = {
          message: this.$t('error-getProspectList'),
          icon: 'data_maintenance.svg'
        };
      }
      this.isLoadingBrokerProspects = false;
    },
    catchUpdatePagination(prop, value) {
      if (prop === 'setPaginationDocumentsPerPage') {
        this.$store.dispatch('setPaginationPageNumber', 1);
      }
      this.$store.dispatch(prop, value);
      this.loadBrokerProspects();
    }
  }
};
</script>

<style scoped>
.broker-details-header{
  justify-content: space-between;
  align-items: flex-end;
  margin: 1rem auto;
}
</style>
